import { yupResolver } from "@hookform/resolvers/yup";
import { ToggleGroupValue, useToast } from "@ligo/layout-design-core";
import { Errors } from "classes";
import { attendanceErrors, dayFormat } from "consts";
import { format, sub } from "date-fns";
import {
  ModalParamsType,
  SearchMailingInDSModel,
  SearchMailingModel,
  SearchOmniModel,
  SubChannelTypeEnum,
} from "models";
import {
  useAttendList,
  useChannel,
  useList,
  useProfile,
  useUser,
} from "providers";
import { useCallback, useMemo, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as AttendanceService from "services/attendance";
import {
  AddAttendanceSchema,
  addTimeSuffix,
  applyMask,
  aytyFormatError,
  removeMask,
} from "utils";

export enum StepAttendanceEnum {
  searchAttendance,
  searchContact,
  searchContactInDS,
  addContact,
}

type AddAttendanceFormType = {
  step: StepAttendanceEnum;
  name: string;
  ddd: string;
  phone: string;
  cpfOrCNPJ: string;
  contract: string;
  channel: number;
  user: number;
  folder: number;
  start: string;
  end: string;
};

type FieldsType = Omit<AddAttendanceFormType, "name" | "ddd">;

const endDate = new Date();
const startDate = sub(endDate, { days: 1 });

const initialFields: FieldsType = {
  step: StepAttendanceEnum.searchAttendance,
  phone: "",
  cpfOrCNPJ: "",
  contract: "",
  channel: 0,
  user: 0,
  folder: 0,
  start: format(startDate, dayFormat),
  end: format(endDate, dayFormat),
};

const { Preview, WhatsApp, Offline } = SubChannelTypeEnum;

export default function AddAttendanceViewModel({
  open,
  onClose,
}: ModalParamsType) {
  const [loading, setLoading] = useState(false);
  const [loadingItem, setLoadingItem] = useState<number>();
  const [fields, setFields] = useState({ ...initialFields });
  const [attendances, setAttendances] = useState<SearchOmniModel[]>();
  const [contactsInDS, setContactsInDS] = useState<SearchMailingInDSModel[]>();
  const [contacts, setContacts] = useState<SearchMailingModel[]>();
  const {
    reset,
    handleSubmit,
    setValue,
    clearErrors,
    register,
    formState: { errors },
  } = useForm({ resolver: yupResolver(AddAttendanceSchema) });
  const { pIdProject, user, defaultSizes } = useProfile();
  const { folderOpts } = useList();
  const { activeUsers } = useUser();
  const { activeChannels } = useChannel();
  const { getAttendances } = useAttendList();
  const { t } = useTranslation("modals");
  const { error, warning, success } = useToast();
  const navigate = useNavigate();

  const errorsResolver = useMemo(
    () => new Errors({ error, warning }, t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const allowedChannels = useMemo(
    () =>
      activeChannels?.filter((c) =>
        [WhatsApp, Preview, Offline].includes(c.IdSubChannelType),
      ),
    [activeChannels],
  );

  const tableIsEmpty = useMemo(() => {
    switch (fields.step) {
      case StepAttendanceEnum.searchAttendance:
        return !attendances?.length;
      case StepAttendanceEnum.searchContact:
        return !contacts?.length;
      case StepAttendanceEnum.searchContactInDS:
        return !contactsInDS?.length;
      default:
        return true;
    }
  }, [fields, attendances, contacts, contactsInDS]);

  const hasTabs = useMemo(() => {
    if (user)
      return {
        searchAttendance: !user?.mustHideQueryOmni,
        searchContact: !user?.mustHideQueryMailing,
        searchDs: user?.hasDialerQuery,
        addContact: !user?.mustHideInsertMailing,
      };
  }, [user]);

  const activeTabs = useMemo(() => {
    const l = [] as StepAttendanceEnum[];

    if (hasTabs?.searchAttendance) l.push(StepAttendanceEnum.searchAttendance);
    if (hasTabs?.searchContact) l.push(StepAttendanceEnum.searchContact);
    if (hasTabs?.searchDs) l.push(StepAttendanceEnum.searchContactInDS);
    if (hasTabs?.addContact) l.push(StepAttendanceEnum.addContact);

    return l;
  }, [hasTabs]);

  const noTabs = useMemo(() => !activeTabs.length, [activeTabs]);

  const isSearchAttendance = useMemo(
    () =>
      fields.step === StepAttendanceEnum.searchAttendance &&
      hasTabs?.searchAttendance,
    [fields, hasTabs],
  );

  const isSearchContact = useMemo(
    () =>
      fields.step === StepAttendanceEnum.searchContact &&
      hasTabs?.searchContact,
    [fields, hasTabs],
  );

  const isSearchContactInDS = useMemo(
    () =>
      fields.step === StepAttendanceEnum.searchContactInDS && hasTabs?.searchDs,
    [fields, hasTabs],
  );

  const isAddContact = useMemo(
    () => fields.step === StepAttendanceEnum.addContact && hasTabs?.addContact,
    [fields, hasTabs],
  );

  const onInit = useCallback(() => {
    setValue("step", fields.step);
    setValue("start", new Date(fields.start));
    setValue("end", new Date(fields.end));
  }, [fields, setValue]);

  const onOpen = useCallback(() => {
    if (open) {
      let currentStep = activeTabs.length ? activeTabs[0] : 0;
      setFields((current) => ({ ...current, step: currentStep }));
    }
  }, [open, activeTabs]);

  const handleChangeField = useCallback(
    (key: keyof FieldsType, value: any) => {
      let newValue = value;

      if (key === "cpfOrCNPJ") {
        newValue = applyMask(
          newValue,
          removeMask(newValue, "cpf").length > 11 ? "cnpj" : "cpf",
        );
      }

      setFields((current) => ({ ...current, [key]: newValue }));
      setValue(key, newValue);
    },
    [setValue],
  );

  const handleChangeStep = useCallback(
    (step: ToggleGroupValue) => {
      handleChangeField("step", step);
      clearErrors();
    },
    [handleChangeField, clearErrors],
  );

  const handleReset = useCallback(() => {
    reset();
    setFields((current) => ({ ...initialFields, step: current.step }));
    setAttendances(undefined);
    setContacts(undefined);
    setContactsInDS(undefined);
  }, [reset]);

  const handleClose = useCallback(
    (id?: number) => {
      handleReset();
      if (id) navigate(id);
      onClose();
    },
    [handleReset, onClose, navigate],
  );

  const validateAddResult = useCallback(
    (idReturnAPI: number, idOmni: number) => {
      switch (true) {
        case idReturnAPI > 0:
          success({ description: t("alerts.successfullyAdded") });
          getAttendances(true);
          handleClose(idOmni);
          break;
        case idReturnAPI === attendanceErrors.alreadyInAttendance:
          warning({ description: t("errors.isAlreadyInAttendance") });
          break;
        case idReturnAPI === attendanceErrors.noVoiceAnswering:
          warning({ description: t("errors.noVoiceAnswering") });
          break;
        case idReturnAPI === attendanceErrors.invalidQueue:
          warning({ description: t("errors.invalidQueue") });
          break;
        case idReturnAPI === attendanceErrors.unassociatedUser:
          warning({ description: t("errors.unassociatedUser") });
          break;
        case idReturnAPI === attendanceErrors.voiceClientExists:
          warning({ description: t("errors.voiceClientExists") });
          break;
        default:
          warning({ description: t("errors.cloudNotStart") });
          break;
      }
    },
    [success, warning, t, getAttendances, handleClose],
  );

  const handleAdd = useCallback(
    async (id: number, alternativeId?: number, idOmniSource?: number) => {
      setLoadingItem(alternativeId ?? id);
      if (pIdProject)
        await AttendanceService.addAttendance({
          pIdProject,
          pIdMailing: id,
          pIdOmniSource: idOmniSource ?? fields.channel,
        })
          .then(({ data }) => validateAddResult(data.idReturnAPI, data.idOmni))
          .catch(errorsResolver.defaultError);
      setLoadingItem(undefined);
    },
    [pIdProject, fields, errorsResolver, validateAddResult],
  );

  const handleAddByDS = useCallback(
    async (id: number, ddd: string, phone: string, nameMailing: string) => {
      setLoadingItem(id);
      if (pIdProject)
        await AttendanceService.addAttendanceByDS({
          pIdProject,
          pIdMailingDialerAPI: id,
          pIdOmniSource: fields.channel,
          pNuDDD: ddd,
          pNuPhone: phone,
          pNmMailing: nameMailing,
        })
          .then(({ data }) => validateAddResult(data.idReturnAPI, data.idOmni))
          .catch(errorsResolver.defaultError);
      setLoadingItem(undefined);
    },
    [pIdProject, fields, errorsResolver, validateAddResult],
  );

  const submit: SubmitHandler<typeof AddAttendanceSchema.__outputType> =
    useCallback(
      async (data) => {
        setLoading(true);
        if (pIdProject)
          switch (data.step) {
            case StepAttendanceEnum.addContact:
              const { name, ddd, phone, channel } = data;
              if (name && ddd && phone && channel)
                await AttendanceService.createContact({
                  pIdProject,
                  pNmMailing: name,
                  pNuDdd: ddd,
                  pNuPhone: phone,
                  pIdOmniSource: channel,
                  pNuRegistration: removeMask(fields.cpfOrCNPJ),
                })
                  .then(async ({ data }) => {
                    if (data.idReturnAPI > 0)
                      await handleAdd(data.idMailing, channel);
                    else warning({ description: t(aytyFormatError(data)) });
                  })
                  .catch(errorsResolver.defaultError);
              break;
            case StepAttendanceEnum.searchContact:
              await AttendanceService.searchContact({
                pIdProject,
                pNmMailing: data.name,
                pNuDdd: data.ddd,
                pNuPhone: data.phone,
                pIdOmniFolder: fields.folder,
                pNuRegistration: removeMask(fields.cpfOrCNPJ),
              })
                .then(({ data }) => {
                  if (data.idReturnAPI > 0) setContacts(data.mailingList);
                  else warning({ description: t(aytyFormatError(data)) });
                })
                .catch(errorsResolver.defaultError);
              break;
            case StepAttendanceEnum.searchContactInDS:
              await AttendanceService.searchContactInDS({
                pIdProject,
                pNmMailing: data.name,
                pNuDdd: data.ddd,
                pNuPhone: data.phone,
                pNuRegistration: removeMask(fields.cpfOrCNPJ),
                pNuContract: fields.contract,
                pIdOmniFolder: fields.folder,
              })
                .then(({ data }) => {
                  if (data.idReturnAPI > 0) setContactsInDS(data.mailingList);
                  else warning({ description: t(aytyFormatError(data)) });
                })
                .catch(errorsResolver.defaultError);
              break;
            case StepAttendanceEnum.searchAttendance:
              await AttendanceService.searchAttendance({
                pIdProject,
                pNmMailing: data.name,
                pNuDdd: data.ddd,
                pNuPhone: data.phone,
                pIdOmniFolder: fields.folder,
                pNuRegistration: removeMask(fields.cpfOrCNPJ),
                pIdUser: fields.user,
                pDtStart: addTimeSuffix(fields.start),
                pDtFinish: addTimeSuffix(fields.end, true),
              })
                .then(({ data }) => {
                  if (data.idReturnAPI > 0) setAttendances(data.omniList);
                  else warning({ description: t(aytyFormatError(data)) });
                })
                .catch(errorsResolver.defaultError);
              break;
            default:
              break;
          }
        setLoading(false);
      },
      [pIdProject, fields, errorsResolver, warning, t, handleAdd],
    );

  return {
    loading,
    loadingItem,
    defaultSizes,
    tableIsEmpty,
    hasTabs,
    noTabs,
    isSearchAttendance,
    isSearchContact,
    isSearchContactInDS,
    isAddContact,
    activeUsers,
    folderOpts,
    allowedChannels,
    fields,
    attendances,
    contacts,
    contactsInDS,
    errors,
    t,
    register,
    onInit,
    onOpen,
    handleChangeStep,
    handleChangeField,
    handleReset,
    handleClose,
    handleAdd,
    handleAddByDS,
    onSubmit: handleSubmit(submit),
  };
}

export type VMReturnType = Pick<
  ReturnType<typeof AddAttendanceViewModel>,
  | "loadingItem"
  | "attendances"
  | "contacts"
  | "contactsInDS"
  | "t"
  | "handleAdd"
  | "handleAddByDS"
>;
